import { useEffect, useState, type ReactNode } from "react";
import { authApi } from "../lib/api";
import { ApiError, clearToken, getRefreshToken, getToken, setAuthTokens } from "../lib/apiClient";
import { clearUser, identifyUser } from "../lib/observability";
import type { User } from "../types/api";
import { AuthContext } from "./authContextValue";

function syncObservabilityUser(user: User | null) {
  if (user) identifyUser(user.id, user.role);
  else clearUser();
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(() => Boolean(getToken()));

  useEffect(() => {
    let active = true;
    const token = getToken();
    if (!token) {
      syncObservabilityUser(null);
      return;
    }
    authApi
      .me()
      .then((me) => {
        if (!active) return;
        setUser(me);
        syncObservabilityUser(me);
      })
      .catch((err: unknown) => {
        // A navigation can abort the in-flight /me call. That rejection is not an
        // auth failure, and clearing storage here logs the next page out.
        if (!active) return;
        if (err instanceof ApiError && err.status === 401) {
          clearToken();
          syncObservabilityUser(null);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  async function login(email: string, password: string) {
    const res = await authApi.login(email, password);
    setAuthTokens(res.access_token, res.refresh_token);
    setUser(res.user);
    syncObservabilityUser(res.user);
    return { user: res.user, accountDeletionRecovered: res.account_deletion_recovered };
  }

  async function register(payload: Parameters<typeof authApi.register>[0]) {
    const res = await authApi.register(payload);
    setAuthTokens(res.access_token, res.refresh_token);
    setUser(res.user);
    syncObservabilityUser(res.user);
    return res.user;
  }

  function applyTokenUser(token: string, refreshToken: string, nextUser: User) {
    setAuthTokens(token, refreshToken);
    setUser(nextUser);
    syncObservabilityUser(nextUser);
  }

  async function refreshUser() {
    const token = getToken();
    if (!token) {
      setUser(null);
      syncObservabilityUser(null);
      return null;
    }
    const me = await authApi.me();
    setUser(me);
    syncObservabilityUser(me);
    return me;
  }

  function logout() {
    const refreshToken = getRefreshToken();
    if (refreshToken) void authApi.logout(refreshToken);
    clearToken();
    setUser(null);
    syncObservabilityUser(null);
  }

  return (
    <AuthContext.Provider
      value={{ user, loading, login, register, applyTokenUser, refreshUser, setUser, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}
